import { MessageDescriptor } from '@lingui/core';
import { defineMessage, plural } from '@lingui/core/macro';
import { type Quantity, type QuantityBaseUnit, type QuantityUnit, QuantityUnitDTOOld } from '@luminovo/api-contracts';
import { isPresent, QuantityHelper } from '@luminovo/commons';
import { formatDecimal, transEnum } from '@luminovo/frontend-utils';

import type { QuantityRenderOption } from '../TanStackTable/type';

type QuantityInput = QuantityUnitDTOOld | Quantity;

type AnyQuantityUnit = QuantityUnit | QuantityBaseUnit;

export type FormatQuantityOptions = {
    /** When true, appends "Pieces" unit label even for cardinal quantities. @default false */
    showPiecesUnit?: boolean;
    maximumFractionDigits?: number;
    /** When true, renders as "PCB"/"PCBs". Takes precedence over other unit options. @default false */
    formatAsPcb?: boolean;
    /** When true, normalizes to base unit before formatting (e.g. 500 mm -> 0.5 m). @default false */
    normalizeToBaseUnit?: boolean;
    /** Column display style from `columnHelper.quantity`. `'base-unit'` implies `normalizeToBaseUnit: true`. */
    renderOption?: QuantityRenderOption;
};

function parseInput(input: QuantityInput, normalizeToBaseUnit: boolean): { quantity: number; unit: AnyQuantityUnit } {
    if ('value' in input) {
        const helper = QuantityHelper.from(input);
        if (normalizeToBaseUnit) {
            const normalized = helper.normalize();
            return { quantity: normalized.parseValue(), unit: normalized.getBaseUnit() };
        }
        return { quantity: helper.parseValue(), unit: input.unit };
    }
    return { quantity: input.quantity, unit: input.unit as QuantityBaseUnit };
}

export function formatQuantity(
    quantityDto: QuantityInput | null | undefined,
    options: FormatQuantityOptions = {},
): string {
    if (!isPresent(quantityDto)) {
        return '-';
    }
    const {
        showPiecesUnit = false,
        maximumFractionDigits = 3,
        formatAsPcb = false,
        normalizeToBaseUnit = options.renderOption === 'base-unit',
    } = options;
    const { unit, quantity } = parseInput(quantityDto, normalizeToBaseUnit);

    if (formatAsPcb) {
        return `${formatDecimal(quantity, { maximumFractionDigits })} ${plural(quantity, { one: 'PCB', zero: 'PCBs', other: 'PCBs' })}`;
    }

    if (unit === 'Pieces' && !showPiecesUnit) {
        return formatDecimal(quantity, { maximumFractionDigits });
    }
    if (quantity === 1) {
        return `${formatDecimal(quantity, { maximumFractionDigits })} ${transEnum(unit, qtyUnitTranslations)}`;
    }
    return `${formatDecimal(quantity, { maximumFractionDigits })} ${transEnum(unit, qtyUnitPluralTranslations)}`;
}

export function formatQuantityUnit(unit?: AnyQuantityUnit | null): string {
    if (!unit) {
        return '-';
    }
    return transEnum(unit, qtyUnitTranslations);
}

const qtyUnitTranslations: Record<AnyQuantityUnit, MessageDescriptor> = {
    Pieces: defineMessage({ message: 'Piece' }),
    Kg: defineMessage({ message: 'Kg' }),
    Kilograms: defineMessage({ message: 'Kilogram' }),
    Grams: defineMessage({ message: 'Gram' }),
    Milligrams: defineMessage({ message: 'Milligram' }),
    Pounds: defineMessage({ message: 'Pound' }),
    Meters: defineMessage({ message: 'Meter' }),
    Centimeters: defineMessage({ message: 'Centimeter' }),
    Millimeters: defineMessage({ message: 'Millimeter' }),
    Feet: defineMessage({ message: 'Foot' }),
    Inches: defineMessage({ message: 'Inch' }),
    Liters: defineMessage({ message: 'Liter' }),
    Centiliters: defineMessage({ message: 'Centiliter' }),
    Milliliters: defineMessage({ message: 'Milliliter' }),
    UsFluidOunces: defineMessage({ message: 'US fl oz' }),
    UsGallons: defineMessage({ message: 'US gallon' }),
    UsPints: defineMessage({ message: 'US pint' }),
};

const qtyUnitPluralTranslations: Record<AnyQuantityUnit, MessageDescriptor> = {
    Pieces: defineMessage({ message: 'Pieces' }),
    Kg: defineMessage({ message: 'Kg' }),
    Kilograms: defineMessage({ message: 'Kilograms' }),
    Grams: defineMessage({ message: 'Grams' }),
    Milligrams: defineMessage({ message: 'Milligrams' }),
    Pounds: defineMessage({ message: 'Pounds' }),
    Meters: defineMessage({ message: 'Meters' }),
    Centimeters: defineMessage({ message: 'Centimeters' }),
    Millimeters: defineMessage({ message: 'Millimeters' }),
    Feet: defineMessage({ message: 'Feet' }),
    Inches: defineMessage({ message: 'Inches' }),
    Liters: defineMessage({ message: 'Liters' }),
    Centiliters: defineMessage({ message: 'Centiliters' }),
    Milliliters: defineMessage({ message: 'Milliliters' }),
    UsFluidOunces: defineMessage({ message: 'US fl oz' }),
    UsGallons: defineMessage({ message: 'US gallons' }),
    UsPints: defineMessage({ message: 'US pints' }),
};
